import React, { ChangeEvent, useContext } from 'react';
import { Link } from 'react-router-dom';

// Hooks
import { useReturnTo } from '../../../hooks/useReturnTo';

// Context.
import { HostContext } from '../../../context/HostContext';
import { InstanceContext } from '../../../context/InstanceContext';

// Hooks
import { useHostForm } from '../../../hooks/useHostForm';

// Constants.
import { HOST_TYPES } from '../../../component/Constants';

// Components.
import Alert from '../../../component/Alert';

// Form Components
import {
    TextInput,
    SelectField,
    CheckboxField,
    PasswordField,
    MultiDropdown
} from '../../../components/forms';

function Detail() {
    const returnFromForm = useReturnTo('/admin/hosts');
    const { hosts, setHosts } = useContext(HostContext);
    const { instances } = useContext(InstanceContext);

    const {
        formState,
        action,
        updateFormData,
        validateName,
        validateHostAddress,
        validatePort,
        handleSubmit,
        canViewPassword
    } = useHostForm({ hosts, setHosts });

    const { formData, validation, uiState, dependencies } = formState;

    // Handle password change with special logic
    const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!formData.passwordChanged) {
            // Just want the character entered.
            updateFormData({
                password: (e.nativeEvent as any).data || '',
                passwordChanged: true
            });
        } else {
            updateFormData({ password: e.target.value });
        }
    };

    React.useEffect(() => {
        
    }, [formData.type])

    // Function to generate dynamic callout text based on host type
    const getCalloutText = () => {
        const hostTypeMapping: { [key: string]: string } = {
            'awsrds': 'aws-rds',
            'linux': 'linux',
            'windows': 'windows'
        };

        const hostTypeNames: { [key: string]: string } = {
            'awsrds': 'AWS RDS',
            'linux': 'Linux',
            'windows': 'Windows'
        };

        const baseText = 'For list of supported platforms see ';
        const urlSlug = hostTypeMapping[formData.type];
        const typeName = hostTypeNames[formData.type];

        if (urlSlug && typeName) {
            return (
                <>
                    {baseText}
                    <a href="https://docs.dbmarlin.com/docs/Getting-Started/supported-platforms" target="_blank" rel="noopener noreferrer">Supported Platforms</a>
                    . For {typeName} prerequisites, see <a href={`https://docs.dbmarlin.com/docs/Monitored-Technologies/Hosts/${urlSlug}`} target="_blank" rel="noopener noreferrer">{typeName}</a>.
                </>
            );
        }

        return (
            <>
                {baseText}
                <a href="https://docs.dbmarlin.com/docs/Getting-Started/supported-platforms" target="_blank" rel="noopener noreferrer">Supported Platforms</a>
            </>
        );
    };

    return (
        <div className="col">
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-server fa-fw" aria-hidden="true"></i>
                    {action.title} host
                </div>
                <div className="card-body">
                    {uiState.isMissing ? (
                        <React.Fragment>
                            <Alert message="The requested host is either invalid or no longer available."
                                   heading="Invalid request" variant="alert-danger"/>
                            <div className="row g-0 form">
                                <div className="col-xs-12 col-md-9 col-lg-6 actions">
                                    <Link to="/admin/hosts/" role="button" className="btn btn-secondary"><i
                                        className="fal fa-arrow-circle-left fa-fw fa-sm"></i><span>Back</span></Link>
                                </div>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>

                            {action.type === 'delete' && (
                                <Alert
                                    message="Please check and ensure you want to delete this host. Any associated instances will remain, and will need to be deleted separately."
                                    heading="Deletes are final and cannot be undone" variant="alert-warning"/>
                            )}

                            {!validation.isFormValid && (
                                <Alert message="Please complete all the required fields below."
                                       heading="Missing required fields" variant="alert-danger"/>
                            )}

                            {/* Callout for supported platforms - only show for create action */}
                            {action.type === 'create' && (
                                <div className="alert alert-info" role="alert">
                                    <i className="fal fa-info-circle fa-fw me-2" aria-hidden="true"></i>
                                    {getCalloutText()}
                                </div>
                            )}

                            <p>Please complete the required details below:</p>

                            <div className="row g-0 form">

                                {/* Title */}
                                <TextInput
                                    id="name"
                                    label="Name"
                                    value={formData.name}
                                    onChange={(value) => updateFormData({ name: value })}
                                    onBlur={validateName}
                                    required
                                    disabled={uiState.disabled}
                                    maxLength={250}
                                    tooltip="The host name"
                                    error={validation.name.isUsed ? "Host name already used." : undefined}
                                    className={validation.name.className}
                                    autoFocus
                                    dataLpIgnore
                                />

                                {/* Host Type */}
                                <SelectField
                                    id="type"
                                    label="Host Type"
                                    value={formData.type}
                                    onChange={(value) => updateFormData({ type: value as string })}
                                    options={HOST_TYPES.map(item => ({
                                        value: item.toLowerCase().replace(/\s/g, ''),
                                        label: item
                                    }))}
                                    required
                                    disabled={uiState.disabled}
                                    placeholder="Select Host Type"
                                    tooltip="Select the host type"
                                    className={validation.type.className}
                                />

                                {/* Host Address */}
                                <TextInput
                                    id="hostAddress"
                                    label={formData.type === 'awsrds' ? 'DB Identifier' : 'Host Address'}
                                    value={formData.hostAddress}
                                    onChange={(value) => updateFormData({ hostAddress: value })}
                                    onBlur={validateHostAddress}
                                    required
                                    disabled={uiState.disabled}
                                    maxLength={250}
                                    tooltip="The host address"
                                    error={validation.hostAddress.isUsed ? "Host address already used." : undefined}
                                    className={validation.hostAddress.className}
                                    dataLpIgnore
                                />


                                {formData.type === 'linux' && (
                                    <>
                                        {/* Port */}
                                        <TextInput
                                            id="port"
                                            label="SSH Port"
                                            value={formData.port.toString()}
                                            onChange={(value) => updateFormData({ port: Number(value) })}
                                            onBlur={validatePort}
                                            required
                                            disabled={uiState.disabled}
                                            maxLength={5}
                                            tooltip="Set the connection port number"
                                            placeholder="Port number, e.g. 22"
                                            className={validation.port.className}
                                            dataLpIgnore
                                        />
                                        <div className="col-xs-12 col-md-6 col-lg-3">
                                            <small className="text-secondary">The SSH port is normally 22 and must be 65,535 or lower.</small>
                                        </div>
                                        <div className="w-100"></div>
                                    </>
                                )}

                                {/* Connection Username */}
                                <TextInput
                                    id="username"
                                    label={formData.type === 'awsrds' ? 'Access Key' : 'Username'}
                                    value={formData.username}
                                    onChange={(value) => updateFormData({ username: value })}
                                    required={formData.type !== 'windows'}
                                    disabled={uiState.disabled}
                                    maxLength={250}
                                    tooltip="Set the connection username"
                                    className={validation.username.className}
                                    dataLpIgnore
                                />

                                {/* Connection Password */}
                                <PasswordField
                                    id="password"
                                    label={formData.type === 'awsrds' ? 'Secret Key' : 'Password'}
                                    value={formData.password}
                                    onChange={handlePasswordChange}
                                    disabled={uiState.disabled}
                                    maxLength={250}
                                    tooltip="Set the connection password"
                                    className={validation.password.className}
                                    canToggleVisibility={canViewPassword()}
                                    dataLpIgnore
                                />
                                <div className="col-xs-12 col-md-6 col-lg-3">
                                    <small className="text-secondary">
                                        {formData.type === 'linux' ? 'Enter a password to use password authentication, or' :
                                        formData.type === 'windows' ? 'Enter a password to use password authentication' :
                                        formData.type === 'awsrds' ? 'Enter the access key and secret key for your IAM user' :
                                        ''}
                                    </small>
                                </div>
                                <div className="w-100"></div>

                                {formData.type === 'linux' && (
                                    <>
                                        {/* PEM */}
                                        <TextInput
                                            id="keyfile"
                                            label="SSH Private Keyfile"
                                            value={formData.keyfile}
                                            onChange={(value) => updateFormData({ keyfile: value })}
                                            disabled={uiState.disabled}
                                            maxLength={250}
                                            tooltip="Set the local SSH PEM keyfile location"
                                            placeholder="PEM File"
                                            className={validation.keyfile.className}
                                            dataLpIgnore
                                        />
                                        <div className="col-xs-12 col-md-6 col-lg-3">
                                            <small className="text-secondary">Enter the full path to the PEM file location on the DBmarlin agent to use Private Key Authentication</small>
                                        </div>
                                        <div className="w-100"></div>
                                    </>
                                )}

                                {formData.type === 'awsrds' && (
                                    <>
                                        {/* Region */}
                                        <TextInput
                                            id="region"
                                            label="AWS Region"
                                            value={formData.region}
                                            onChange={(value) => updateFormData({ region: value })}
                                            required={formData.type === 'awsrds'}
                                            disabled={uiState.disabled}
                                            maxLength={250}
                                            tooltip="Set the AWS region"
                                            placeholder="Region"
                                            className={validation.region.className}
                                            dataLpIgnore
                                        />
                                    </>
                                )}

                                {/* Monitoring Interval  */}
                                <SelectField
                                    id="monitoring-interval"
                                    label="Monitoring Interval (milliseconds)"
                                    value={formData.monitoringInterval}
                                    onChange={(value) => updateFormData({ monitoringInterval: Number(value) })}
                                    options={[100, 200, 500, 1000, 2000, 5000, 10000, 20000, 30000, 60000].map(val => ({
                                        value: val,
                                        label: val.toString()
                                    }))}
                                    required
                                    disabled={uiState.disabled || formData.type === 'awsrds'}
                                    tooltip="Set the monitoring interval"
                                />

                                {/* Agent */}
                                <SelectField
                                    id="agentId"
                                    label="Agent"
                                    value={formData.agentId}
                                    onChange={(value) => updateFormData({ agentId: Number(value) })}
                                    options={dependencies.agents.map(agent => ({
                                        value: agent.id,
                                        label: `${agent.name} (${agent.hostname})`
                                    }))}
                                    required
                                    disabled={uiState.disabled}
                                    tooltip="The agent"
                                    className={validation.agentId.className}
                                />

                                {/* Debug Mode */}
                                <CheckboxField
                                    id="debug"
                                    label="Debug Enabled"
                                    checked={formData.debug}
                                    onChange={(checked) => updateFormData({ debug: checked })}
                                    disabled={uiState.disabled}
                                    tooltip="Enable debug mode"
                                />

                                {/* Instance Mappings */}
                                <div className="col-xs-12 col-md-3">
                                    <label htmlFor="mappings" className="reminder"
                                           data-tooltip-id="global-tooltip" data-tooltip-content="Map this host to one or more instances">Instance Mappings</label>
                                    <span className="badge bg-info"
                                          data-tooltip-id="global-tooltip" data-tooltip-content="Total mapped instance count">{formData.mappedInstances.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                                </div>
                                <div className="col-xs-12 col-md-6 col-lg-4">
                                    <MultiDropdown
                                        value={formData.mappedInstances}
                                        options={[...instances].sort((a, b) => a.name.localeCompare(b.name)).map(instance => ({ value: instance.id, label: instance.name }))}
                                        onChange={(value) => updateFormData({ mappedInstances: value })}
                                        placeholder="No instances mapped"
                                        filterPlaceholder="Search instances"
                                        disabled={uiState.disabled}
                                    />
                                </div>
                                <div className="w-100"></div>

                                {/* Actions */}
                                <div className="col-xs-12 col-md-9 col-lg-7 actions">
                                    <button onClick={returnFromForm} className="btn btn-secondary"><i
                                        className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span></button>
                                    <button
                                        disabled={uiState.loading}
                                        className={`btn btn-${action.variant}`}
                                        onClick={handleSubmit}>
                                        <i className={`${action.icon} fa-fw fa-sm`}></i><span>{action.title}</span>
                                    </button>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Detail;
